import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  getTopicGroupsInABatch,
  getConceptsTopicWiseInTGInBatch,
  getBatchByUID
} from '@selectors/batches';
import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import H4 from '@base/H4/H4';
import { useSnackbar } from '@stores/snackbar';
import TopicGroupCard from '@comp/TopicGroupCard/TopicGroupCard';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { useDispatch } from 'react-redux';
import { Spacings, breakpoints } from '@styles/index';
import { fetchTopicsWithChildren } from '@actions/batches';

const TopicGroupsWrapper = styled.div`
  position: relative;
`;

const TopicGroups = styled.div`
  position: sticky;
  top: 224px;
  width: 224px;
  z-index: 1;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: flex;
    width: 100%;
    overflow: scroll;
    white-space: nowrap;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding: 12px 0;
    position: sticky;
    top: 48px;
    background-color: var(--color-i-light-gray);
  }
`;

const TopicGroupName = styled(P1)`
  margin-bottom: ${Spacings.SPACING_1B};
  margin-top: ${({ index }) => index && Spacings.SPACING_1B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  cursor: pointer;
  font-weight: ${({ isSelected }) => (isSelected ? 'bold' : 'normal')};
  border: ${({ isSelected }) => isSelected && '1px solid var(--color-shadow)'};
  border-radius: ${Spacings.SPACING_2B};
  background-color: ${({ isSelected }) => isSelected && 'var(--color-base-1)'};
  color: ${({ isSelected }) => isSelected && 'var(--color-text-primary)'};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    border: 1px solid var(--color-divider);
    background-color: ${({ isSelected }) =>
      isSelected && 'var(--color-text-primary)'};
    color: ${({ isSelected }) => isSelected && 'var(--color-base-1)'};
    margin: 0 ${Spacings.SPACING_2B} 0 0;
    font-weight: 400;
  }
`;

const Topics = styled.div`
  grid-column: 3/25;
  display: grid;
  grid-template-columns: repeat(18, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: 110px;
  min-height: 800px;
  align-content: flex-start;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin-top: 28px;
    display: flex;
    flex-direction: column;
  }
`;

const TopicName = styled(H4)`
  grid-column: span 16;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${({ index }) => index !== 0 && Spacings.SPACING_16B};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: ${({ index }) => index !== 0 && Spacings.SPACING_12B};
  }
`;

const Wrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: flex;
    flex-direction: column;
    grid-column-gap: 8px;
  }
`;

const Card = styled(TopicGroupCard)`
  margin-bottom: ${Spacings.SPACING_4B};
`;

const TopicsContainer = ({ topicUID, batchUID }) => {
  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();
  const [ntopicUID, setNtopicUID] = useState(topicUID);
  const topicGroups = useSelectorToStore(getTopicGroupsInABatch(batchUID));
  const topicsWithConcepts = useSelectorToStore(
    getConceptsTopicWiseInTGInBatch(batchUID, ntopicUID)
  );
  const batchData = useSelectorToStore(getBatchByUID(batchUID));

  const selectedTopicGroup = topicGroups?.findIndex(
    ({ uid }) => ntopicUID === uid
  );
  const handleTopicGroupChange = (uid) => () => {
    dispatch(fetchTopicsWithChildren(batchUID, uid)).then((res) => {
      if (res.error) {
        showSnackbar({
          message: res.error?.message || 'Something went wrong.Try again later',
          type: 'error'
        });
        return;
      }
      setNtopicUID(uid);
      if (batchData)
        window.history.replaceState(
          {
            ...window.history.state,
            as: `/batch/${batchData.slug}/${batchData.uid}/topics/${uid}`,
            url: `/batch/${batchData.slug}/${batchData.uid}/topics/${uid}`
          },
          '',
          `/batch/${batchData.slug}/${batchData.uid}/topics/${uid}`
        );
      // Need a better way
      // pushRoute(
      //   '/batch/[slug]/[batchUID]/topics/[topicUID]',
      //   `/batch/${batchData.slug}/${batchData.uid}/topics/${uid}`,
      //   {
      //     shallow: true
      //   },
      //   null
      // );
    });
  };
  return (
    <>
      <Wrapper>
        {topicGroups ? (
          <TopicGroupsWrapper>
            <TopicGroups>
              {topicGroups?.map(({ name, uid }, index) => {
                return (
                  <TopicGroupName
                    color="var(--color-text-secondary)"
                    key={uid}
                    onClick={handleTopicGroupChange(uid)}
                    index={index}
                    isSelected={index === selectedTopicGroup}
                  >
                    {name}
                  </TopicGroupName>
                );
              })}
            </TopicGroups>
          </TopicGroupsWrapper>
        ) : null}
        <Topics>
          {topicsWithConcepts &&
            topicsWithConcepts.map((topic, index) => (
              <>
                <TopicName key={topic.uid} index={index}>
                  {topic.name}
                </TopicName>
                {topic?.children?.map((concept) => (
                  <Card
                    topology={concept}
                    key={concept.uid}
                    courseType="concept"
                    color={
                      concept.has_lessons
                        ? concept.color
                        : 'var(--color-i-blue)'
                    }
                    batch={batchData}
                    topicUID={topic.uid}
                    disabled={!concept.has_lessons}
                  />
                ))}
              </>
            ))}
        </Topics>
      </Wrapper>
    </>
  );
};

export default TopicsContainer;

TopicsContainer.propTypes = {
  topicUID: PropTypes.string.isRequired,
  batchUID: PropTypes.string.isRequired
};
